import { Fragment, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { SuggestedChangesView } from '@/components/SuggestChanges';
import { usePartySuggestChanges } from 'src/context/suggest-changes';
import { Button } from '@/components/ui/button';
import { ClientError, getApiError } from 'src/utils/errors';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { getRequestChangesExitPath } from 'src/pages/SoftwareEscrowRequestChanges/lib';

import {
  getOrganizationSuggestedChangesItems,
  getEditedContactsSuggestedChangesItems,
  getSignatorySuggestedChangesItems,
  getNewContactsSuggestedChangesItems,
  getDeletedContactsSuggestedChangesItems,
} from '../lib';

import type { IBeneficiary, IDepositor } from 'src/graphql/schema';

const SubmitChangesDialog = () => {
  const { partyType, submitChangesDialog, setSubmitChangesDialog, createSuggestedChanges, suggestedChanges } =
    usePartySuggestChanges();
  const {
    softwareEscrow,
    softwareEscrow: { id },
    refetch,
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);

  const party = softwareEscrow[partyType] as IBeneficiary | IDepositor;

  const onSubmit = async () => {
    setIsLoading(true);

    try {
      const { data } = await createSuggestedChanges();

      if (data?.suggestedChangeCreate?.success) {
        setSubmitChangesDialog(false);
        setIsConfirmationDialogOpen(true);
      } else {
        throw new ClientError(data?.suggestedChangeCreate?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    } finally {
      setIsLoading(false);
    }
  };

  const organizationSuggestedChangesItems = getOrganizationSuggestedChangesItems(suggestedChanges.organization);
  const editedContactsSuggestedChangesItems = getEditedContactsSuggestedChangesItems(
    suggestedChanges.editedContacts,
    party?.contacts ?? [],
  );
  const signatorySuggestedChangesItems = getSignatorySuggestedChangesItems(
    suggestedChanges.editedContacts,
    party?.contacts ?? [],
  );
  const newContactsSuggestedChangesItems = getNewContactsSuggestedChangesItems(suggestedChanges.newContacts);
  const deletedContactsSuggestedChangesItems = getDeletedContactsSuggestedChangesItems(
    suggestedChanges.deletedContacts,
  );

  const suggestedChangesItems = [
    ...organizationSuggestedChangesItems,
    ...editedContactsSuggestedChangesItems,
    ...signatorySuggestedChangesItems,
    ...newContactsSuggestedChangesItems,
    ...deletedContactsSuggestedChangesItems,
  ];

  const onConfirmationDialogOpenChange = async (open: boolean) => {
    setIsConfirmationDialogOpen(open);

    if (!open) {
      await navigate(getRequestChangesExitPath(id, partyType));
      refetch();
    }
  };

  return (
    <Fragment>
      <AlertDialog open={submitChangesDialog} onOpenChange={setSubmitChangesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to submit these changes?</AlertDialogTitle>
            <AlertDialogDescription>
              These changes will be sent for Codekeeper’s review. Depending on the nature of the suggestion, the changes
              may require the counterpart’s permission before they can be applied, and signed agreements may have to be
              legally amended.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <SuggestedChangesView defaultOpen items={suggestedChangesItems} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button disabled={isLoading} isLoading={isLoading} onClick={onSubmit}>
              Submit
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={isConfirmationDialogOpen} onOpenChange={onConfirmationDialogOpenChange}>
        <DialogContent className="sm:max-w-[441px]">
          <DialogHeader className="gap-1.5 text-center">
            <DialogTitle>Suggested changes sent for review!</DialogTitle>
            <DialogDescription>
              We will notify you of the outcome as soon as the review has been completed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button className="w-full">Back to escrow</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

export default SubmitChangesDialog;
